import { useEffect, useState, useRef, useCallback } from 'react';
import { api, ApiError } from '../lib/api';
import { RiskBadge } from '../components/RiskBadge';
import { Skel } from '../components/Skel';
import { Banner } from '../components/Banner';
import { Crumbs } from '../components/Crumbs';
import { EmptyState } from '../components/EmptyState';
import type { CompareResponse, SuggestItem } from '../data/types';

interface CompareProps {
  /** Initial ИНН-list из URL (?inns=A,B,C). */
  initialInns?: string[];
}

const INN_RE = /^\d{10}$|^\d{12}$/;

/**
 * Сравнение 2-5 компаний side-by-side.
 *
 * Layout:
 *  - Crumbs + H1
 *  - Form: 5 input slot'ов с autocomplete (api.suggest), кнопка «Сравнить»
 *  - Результат: горизонтальная таблица — каждая колонка = одна компания,
 *    строки = ключевые поля (название, ИНН, ОГРН, риск, выручка, налоги,
 *    санкции, реестры).
 *  - Summary block: highest/lowest риск, oldest/newest, any_sanctioned, etc.
 *
 * URL state: #/compare?inns=A,B,C — синхронизируется с инпутами при «Сравнить».
 *
 * Mobile-first: на узких экранах таблица скроллится по горизонтали.
 */
export function Compare({ initialInns = [] }: CompareProps) {
  const [slots, setSlots] = useState<string[]>(() => {
    const padded = [...initialInns];
    while (padded.length < 3) padded.push('');
    return padded.slice(0, 5);
  });
  const [result, setResult] = useState<CompareResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const performCompare = useCallback((inns: string[]) => {
    const valid = inns.filter((i) => INN_RE.test(i));
    const unique = Array.from(new Set(valid));
    if (unique.length < 2) {
      setError('Минимум 2 уникальных валидных ИНН (10 или 12 цифр)');
      setResult(null);
      return;
    }
    if (unique.length > 5) {
      setError('Максимум 5 компаний для сравнения');
      return;
    }
    setLoading(true);
    setError(null);
    const ac = new AbortController();
    api
      .compare(unique, { signal: ac.signal })
      .then(setResult)
      .catch((e) => {
        if (e.name !== 'AbortError') {
          setError(e instanceof ApiError ? `API ${e.status}` : String(e));
        }
      })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, []);

  // Initial compare если в URL пришли ИНН
  useEffect(() => {
    if (initialInns.length >= 2) {
      performCompare(initialInns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const inns = slots.map((s) => s.trim()).filter(Boolean);
    performCompare(inns);
    // Sync URL
    const next = `#/compare?inns=${inns.join(',')}`;
    if (window.location.hash !== next) {
      window.history.replaceState(null, '', next);
    }
  }

  function setSlot(idx: number, value: string) {
    setSlots((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  }

  function addSlot() {
    if (slots.length < 5) setSlots((prev) => [...prev, '']);
  }

  function removeSlot(idx: number) {
    if (slots.length <= 2) return;
    setSlots((prev) => prev.filter((_, i) => i !== idx));
  }

  return (
    <main className="container" style={{ paddingTop: 32 }}>
      <Crumbs
        items={[
          { label: 'Главная', href: '#/' },
          { label: 'Сравнение' },
        ]}
      />

      <h1 style={{ marginTop: 16, marginBottom: 8 }}>Сравнение компаний</h1>
      <p style={{ color: 'var(--muted, #6b7287)', marginTop: 0, marginBottom: 24 }}>
        Введите 2–5 ИНН компаний для сравнения характеристик и рисков side-by-side.
      </p>

      {/* INPUT SLOTS */}
      <form onSubmit={onSubmit} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {slots.map((value, idx) => (
            <CompareSlotInput
              key={idx}
              index={idx}
              value={value}
              canRemove={slots.length > 2}
              onChange={(v) => setSlot(idx, v)}
              onRemove={() => removeSlot(idx)}
            />
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 12,
            flexWrap: 'wrap',
          }}
        >
          {slots.length < 5 && (
            <button
              type="button"
              onClick={addSlot}
              style={{
                padding: '10px 16px',
                background: 'transparent',
                color: 'var(--navy-700, #0b1736)',
                border: '1px dashed var(--border, #c9c5b6)',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              + добавить компанию ({slots.length}/5)
            </button>
          )}
          <button
            type="submit"
            style={{
              padding: '10px 24px',
              background: 'var(--navy-700, #0b1736)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 15,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            Сравнить
          </button>
        </div>
      </form>

      {error && (
        <Banner tone="risk" title="Ошибка">
          {error}
        </Banner>
      )}

      {loading && (
        <div style={{ marginTop: 16 }}>
          <Skel count={5} height={48} />
        </div>
      )}

      {!loading && !error && result && <CompareResultTable result={result} />}

      {!loading && !error && !result && (
        <EmptyState
          icon="⚖"
          title="Сравнение ещё не запущено"
          body="Введите ИНН и нажмите «Сравнить». Можно подставить ИНН известных компаний (например, 7706107510 — Роснефть, 7736050003 — Газпром)."
        />
      )}
    </main>
  );
}

function CompareSlotInput({
  index,
  value,
  canRemove,
  onChange,
  onRemove,
}: {
  index: number;
  value: string;
  canRemove: boolean;
  onChange: (v: string) => void;
  onRemove: () => void;
}) {
  const [suggestions, setSuggestions] = useState<SuggestItem[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (value.trim().length < 2 || INN_RE.test(value.trim())) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      const ac = new AbortController();
      api
        .suggest(value.trim(), 5, { signal: ac.signal })
        .then((r) => setSuggestions(r.items))
        .catch(() => {});
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [value]);

  return (
    <div style={{ position: 'relative', display: 'flex', gap: 8, alignItems: 'center' }}>
      <span
        style={{
          minWidth: 28,
          textAlign: 'center',
          fontFamily: 'JetBrains Mono, monospace',
          color: 'var(--muted, #6b7287)',
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        {index + 1}.
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 200)}
        placeholder="ИНН или название компании"
        aria-label={`ИНН компании ${index + 1}`}
        autoComplete="off"
        style={{
          flex: 1,
          minWidth: 0,
          padding: '10px 14px',
          fontSize: 14,
          border: '1px solid var(--border, #e6e2d6)',
          borderRadius: 8,
          background: 'var(--surface, #ffffff)',
        }}
      />
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Удалить из сравнения"
          style={{
            width: 32,
            height: 32,
            border: 'none',
            background: 'transparent',
            color: 'var(--muted, #6b7287)',
            cursor: 'pointer',
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      )}
      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 36,
            right: canRemove ? 40 : 0,
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e6e2d6)',
            borderRadius: 8,
            listStyle: 'none',
            margin: '4px 0 0',
            padding: 4,
            zIndex: 10,
            maxHeight: 240,
            overflowY: 'auto',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}
        >
          {suggestions.map((s) => (
            <li
              key={s.slug}
              role="option"
              aria-selected="false"
              style={{
                padding: '8px 10px',
                cursor: 'pointer',
                borderRadius: 6,
                fontSize: 13,
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(s.inn);
                setOpen(false);
              }}
              onMouseEnter={(e) =>
                ((e.target as HTMLLIElement).style.background = 'var(--bg, #fbfaf7)')
              }
              onMouseLeave={(e) =>
                ((e.target as HTMLLIElement).style.background = 'transparent')
              }
            >
              <div style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{s.name_short}</div>
              <div style={{ color: 'var(--muted, #6b7287)', fontSize: 12 }}>
                ИНН {s.inn}
                {s.region_name && ` · ${s.region_name}`}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CompareResultTable({ result }: { result: CompareResponse }) {
  const items = result.items;
  return (
    <section aria-label="Результат сравнения">
      {/* Summary banner */}
      <div
        style={{
          background: 'var(--surface, #ffffff)',
          border: '1px solid var(--border, #e6e2d6)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 20,
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        }}
      >
        <SummaryStat
          label="Найдено"
          value={`${result.summary.found} из ${result.summary.requested}`}
        />
        {result.summary.highest_risk !== null && (
          <SummaryStat label="Макс. риск" value={String(result.summary.highest_risk)} />
        )}
        {result.summary.lowest_risk !== null && (
          <SummaryStat label="Мин. риск" value={String(result.summary.lowest_risk)} />
        )}
        {result.summary.oldest_age_years !== null && (
          <SummaryStat
            label="Старейшая"
            value={`${result.summary.oldest_age_years.toFixed(1)} лет`}
          />
        )}
        <SummaryStat
          label="Под санкциями"
          value={result.summary.any_sanctioned ? 'Есть' : 'Нет'}
          tone={result.summary.any_sanctioned ? 'risk' : 'ok'}
        />
        <SummaryStat
          label="Массовый адрес"
          value={result.summary.any_mass_address ? 'Есть' : 'Нет'}
          tone={result.summary.any_mass_address ? 'warn' : 'ok'}
        />
      </div>

      {/* Side-by-side table */}
      <div
        style={{
          background: 'var(--surface, #ffffff)',
          border: '1px solid var(--border, #e6e2d6)',
          borderRadius: 12,
          overflow: 'auto',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ ...thLabelStyle, position: 'sticky', left: 0, background: 'var(--surface-quiet, #f5f3ec)' }}>
                Параметр
              </th>
              {items.map((it) => (
                <th key={it.inn} style={thColStyle}>
                  {it.found ? (
                    <a
                      href={`#/company/${it.slug}`}
                      style={{
                        color: 'var(--navy-700, #0b1736)',
                        textDecoration: 'none',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {it.name_short}
                    </a>
                  ) : (
                    <span style={{ color: 'var(--muted, #6b7287)' }}>Не найдена</span>
                  )}
                  <div
                    style={{
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: 11,
                      color: 'var(--muted, #6b7287)',
                      fontWeight: 400,
                      marginTop: 4,
                    }}
                  >
                    {it.inn}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <Row
              label="Регион"
              cells={items.map((it) => (
                <span>{it.region_name ?? '—'}</span>
              ))}
              items={items}
            />
            <Row
              label="ОКВЭД"
              cells={items.map((it) =>
                it.okved_main_code ? (
                  <span style={{ overflowWrap: 'anywhere', display: 'inline-block' }}>
                    <code style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {it.okved_main_code}
                    </code>
                    {it.okved_main_name && (
                      <span
                        style={{
                          color: 'var(--muted, #6b7287)',
                          fontSize: 12,
                          marginLeft: 4,
                        }}
                      >
                        {it.okved_main_name}
                      </span>
                    )}
                  </span>
                ) : (
                  <span>—</span>
                )
              )}
              items={items}
            />
            <Row
              label="Возраст"
              cells={items.map((it) => (
                <span>{it.age_years !== null ? `${it.age_years.toFixed(1)} лет` : '—'}</span>
              ))}
              items={items}
            />
            <Row
              label="Статус"
              cells={items.map((it) => (
                <span style={{ color: it.status === 'active' ? '#1f6b34' : '#7a5a10' }}>
                  {it.status ?? '—'}
                </span>
              ))}
              items={items}
            />
            <Row
              label="Риск"
              cells={items.map((it) =>
                it.risk?.level ? (
                  <RiskBadge level={it.risk.level} score={it.risk.score} />
                ) : (
                  <span>—</span>
                )
              )}
              items={items}
            />
            <Row
              label="Выручка"
              cells={items.map((it) => (
                <strong>{fmtRevenue(it.finance?.revenue ?? null)}</strong>
              ))}
              items={items}
            />
            <Row
              label="Прибыль"
              cells={items.map((it) => (
                <span>{fmtRevenue(it.finance?.profit ?? null)}</span>
              ))}
              items={items}
            />
            <Row
              label="Сотрудники"
              cells={items.map((it) => (
                <span>{it.finance?.employees ?? '—'}</span>
              ))}
              items={items}
            />
            <Row
              label="Санкции"
              cells={items.map((it) =>
                it.sanctions.active > 0 ? (
                  <span style={{ color: '#a31919', fontWeight: 600 }}>
                    {it.sanctions.active} активных
                    <div style={{ fontSize: 11, fontWeight: 400, marginTop: 2 }}>
                      {it.sanctions.lists.slice(0, 3).join(', ')}
                      {it.sanctions.lists.length > 3 && ` +${it.sanctions.lists.length - 3}`}
                    </div>
                  </span>
                ) : (
                  <span style={{ color: '#1f6b34' }}>Нет</span>
                )
              )}
              items={items}
            />
            <Row
              label="Активные суды"
              cells={items.map((it) => (
                <span
                  style={{
                    color: (it.signals?.active_lawsuits ?? 0) > 0 ? '#a31919' : 'var(--muted)',
                  }}
                >
                  {it.signals?.active_lawsuits ?? 0}
                </span>
              ))}
              items={items}
            />
            <Row
              label="Исп. произв."
              cells={items.map((it) => (
                <span
                  style={{
                    color: (it.signals?.active_enforcement ?? 0) > 0 ? '#a31919' : 'var(--muted)',
                  }}
                >
                  {it.signals?.active_enforcement ?? 0}
                </span>
              ))}
              items={items}
            />
            <Row
              label="Массовый адрес"
              cells={items.map((it) => (
                <FlagCell on={it.flags?.mass_address === true} label="Да" off="Нет" warn />
              ))}
              items={items}
            />
            <Row
              label="Ненадёж. рук-ль"
              cells={items.map((it) => (
                <FlagCell on={it.flags?.unreliable_director === true} label="Да" off="Нет" warn />
              ))}
              items={items}
            />
            <Row
              label="IT-аккредит."
              cells={items.map((it) => (
                <FlagCell on={it.flags?.is_it_accredited === true} label="Да" off="—" />
              ))}
              items={items}
            />
            <Row
              label="МСП категория"
              cells={items.map((it) => (
                <span>{it.flags?.msp_category ?? '—'}</span>
              ))}
              items={items}
            />
            <Row
              label="Рейтинг"
              cells={items.map((it) => (
                <span>
                  {it.reputation?.rating ?? '—'}{' '}
                  {it.reputation?.reviews_count ? (
                    <span style={{ color: 'var(--muted, #6b7287)', fontSize: 12 }}>
                      ({it.reputation.reviews_count})
                    </span>
                  ) : null}
                </span>
              ))}
              items={items}
            />
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Row({
  label,
  cells,
  items,
}: {
  label: string;
  cells: React.ReactNode[];
  items: CompareResponse['items'];
}) {
  return (
    <tr>
      <td style={tdLabelStyle}>{label}</td>
      {cells.map((cell, i) => (
        <td
          key={items[i]?.inn ?? i}
          style={{
            ...tdStyle,
            opacity: items[i]?.found ? 1 : 0.4,
          }}
        >
          {items[i]?.found ? cell : <span style={{ color: 'var(--muted, #6b7287)' }}>—</span>}
        </td>
      ))}
    </tr>
  );
}

function FlagCell({
  on,
  label,
  off = 'Нет',
  warn = false,
}: {
  on: boolean;
  label: string;
  off?: string;
  warn?: boolean;
}) {
  if (!on) return <span style={{ color: 'var(--muted, #6b7287)' }}>{off}</span>;
  return (
    <span
      style={{
        color: warn ? '#a31919' : '#1f6b34',
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

function SummaryStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn' | 'risk';
}) {
  const color = tone === 'risk' ? '#a31919' : tone === 'warn' ? '#9b5118' : tone === 'ok' ? '#1f6b34' : 'var(--text, #0e1320)';
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--muted, #6b7287)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color, marginTop: 4, overflowWrap: 'anywhere' }}>
        {value}
      </div>
    </div>
  );
}

const thLabelStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 14px',
  borderBottom: '2px solid var(--gold-500, #c79a2c)',
  fontFamily: 'Manrope, sans-serif',
  fontSize: 13,
  color: 'var(--muted, #6b7287)',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  zIndex: 1,
};
const thColStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 14px',
  borderBottom: '2px solid var(--gold-500, #c79a2c)',
  borderLeft: '1px solid var(--border, #e6e2d6)',
  fontFamily: 'Manrope, sans-serif',
  fontSize: 14,
  color: 'var(--navy-700, #0b1736)',
  fontWeight: 600,
  minWidth: 180,
  verticalAlign: 'top',
};
const tdLabelStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderBottom: '1px solid var(--border, #e6e2d6)',
  color: 'var(--muted, #6b7287)',
  fontSize: 13,
  whiteSpace: 'nowrap',
  position: 'sticky',
  left: 0,
  background: 'var(--surface, #ffffff)',
  zIndex: 1,
};
const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderBottom: '1px solid var(--border, #e6e2d6)',
  borderLeft: '1px solid var(--border, #e6e2d6)',
  fontSize: 13,
  verticalAlign: 'top',
  minWidth: 180,
  overflowWrap: 'anywhere',
};

function fmtRevenue(value: string | null): string {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1_000_000_000_000) return `${(n / 1_000_000_000_000).toFixed(1)} трлн ₽`;
  if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} млрд ₽`;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} млн ₽`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(0)} тыс ₽`;
  return `${n.toLocaleString('ru-RU')} ₽`;
}
